// src/admin/customers/CustomerDrawer.jsx
import React, { useEffect } from "react";

// ── Shared helpers ──────────────────────────────────────────────────────────

const STATUS_BADGES = {
  active:    { label: "Active",    bg: "#D1FAE5", color: "#059669" },
  flagged:   { label: "Flagged",   bg: "#FEE2E2", color: "#DC2626" },
  suspended: { label: "Suspended", bg: "#F1F5F9", color: "#64748B" },
};

const BOOKING_STATUS_STYLES = {
  completed:   { label: "Completed",   color: "#94A3B8" },
  confirmed:   { label: "Confirmed",   color: "#6366F1" },
  in_progress: { label: "In Progress", color: "#0EA5E9" },
  pending:     { label: "Pending",     color: "#D97706" },
  cancelled:   { label: "Cancelled",   color: "#DC2626" },
};

function avatarBg(name) {
  const palette = ["#EEF2FF", "#F0FDF4", "#FEF3C7", "#FEE2E2", "#E0F2FE", "#FCE7F3", "#F5F3FF"];
  return palette[name.charCodeAt(0) % palette.length];
}

function avatarColor(name) {
  const palette = ["#4338CA", "#16A34A", "#D97706", "#DC2626", "#0369A1", "#BE185D", "#7C3AED"];
  return palette[name.charCodeAt(0) % palette.length];
}

function initials(name) {
  return name.split(" ").map((n) => n[0]).slice(0, 2).join("");
}

function relativeTime(isoString) {
  const diff = Math.floor((Date.now() - new Date(isoString)) / 86400000);
  if (diff === 0) return "Today";
  if (diff === 1) return "Yesterday";
  return `${diff}d ago`;
}

// ── Section header ──────────────────────────────────────────────────────────

function SectionLabel({ children }) {
  return (
    <div
      style={{
        fontSize: 11,
        fontWeight: 700,
        color: "#94A3B8",
        textTransform: "uppercase",
        letterSpacing: "0.07em",
        marginBottom: 12,
        fontFamily: "'Plus Jakarta Sans', sans-serif",
      }}
    >
      {children}
    </div>
  );
}

// ── Detail row ──────────────────────────────────────────────────────────────

function DetailRow({ label, value, mono }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "9px 0",
        borderBottom: "1px solid #F8FAFC",
      }}
    >
      <span
        style={{
          fontSize: 13,
          color: "#94A3B8",
          fontWeight: 500,
          fontFamily: "'Plus Jakarta Sans', sans-serif",
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: 13,
          color: "#334155",
          fontWeight: 600,
          fontFamily: mono ? "monospace" : "'Plus Jakarta Sans', sans-serif",
        }}
      >
        {value}
      </span>
    </div>
  );
}

// ── Main component ──────────────────────────────────────────────────────────

export default function CustomerDrawer({ customer, onClose, onUpdate, showToast }) {
  useEffect(() => {
    const handleEscape = (e) => { if (e.key === "Escape") onClose(); };
    if (customer) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [customer, onClose]);

  if (!customer) return null;

  const handleFlag = () => {
    const newStatus = customer.status === "flagged" ? "active" : "flagged";
    onUpdate({ ...customer, status: newStatus });
    showToast(
      `${customer.name} ${newStatus === "flagged" ? "flagged" : "unflagged"}`,
      newStatus === "flagged" ? "error" : "success"
    );
  };

  const handleSuspend = () => {
    const newStatus = customer.status === "suspended" ? "active" : "suspended";
    onUpdate({ ...customer, status: newStatus });
    showToast(
      `${customer.name} ${newStatus === "suspended" ? "suspended" : "reactivated"}`,
      "success"
    );
  };

  const badge = STATUS_BADGES[customer.status];
  const avgSpend = customer.totalOrders > 0
    ? Math.round(customer.totalSpend / customer.totalOrders)
    : 0;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(15,23,42,0.35)",
          backdropFilter: "blur(2px)",
          zIndex: 199,
        }}
      />

      {/* Panel */}
      <div
        style={{
          position: "fixed",
          right: 0,
          top: 0,
          bottom: 0,
          width: 420,
          background: "#fff",
          boxShadow: "-4px 0 40px rgba(0,0,0,0.14)",
          zIndex: 200,
          display: "flex",
          flexDirection: "column",
          height: "100vh",
        }}
      >
        {/* ── Header ── */}
        <div
          style={{
            flexShrink: 0,
            height: 60,
            background: "#F8FAFC",
            borderBottom: "1px solid #E2E8F0",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "0 24px",
          }}
        >
          <div
            style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: 15,
              fontWeight: 700,
              color: "#0F172A",
            }}
          >
            Customer Profile
          </div>
          <button
            onClick={onClose}
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              border: "1px solid #E2E8F0",
              background: "#fff",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#64748B" strokeWidth="2">
              <line x1="18" y1="6"  x2="6"  y2="18" />
              <line x1="6"  y1="6"  x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* ── Body (scrollable) ── */}
        <div style={{ flex: 1, overflowY: "auto", padding: "24px" }}>

          {/* Identity block */}
          <div style={{ display: "flex", gap: 14, marginBottom: 20 }}>
            <div
              style={{
                width: 52,
                height: 52,
                borderRadius: 14,
                background: avatarBg(customer.name),
                color: avatarColor(customer.name),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 18,
                fontWeight: 700,
                flexShrink: 0,
              }}
            >
              {initials(customer.name)}
            </div>
            <div style={{ minWidth: 0 }}>
              {/* Name — always shown, always primary */}
              <div
                style={{
                  fontSize: 18,
                  fontWeight: 800,
                  color: "#0F172A",
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  letterSpacing: "-0.3px",
                  marginBottom: 2,
                }}
              >
                {customer.name}
              </div>

              {/* Phone — primary identifier, always visible */}
              <div
                style={{
                  fontSize: 13.5,
                  fontWeight: 600,
                  color: "#334155",
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  marginBottom: customer.email ? 4 : 8,
                }}
              >
                {customer.phone}
              </div>

              {/* Email — only rendered when it exists, no placeholder */}
              {customer.email && (
                <div
                  style={{
                    fontSize: 12,
                    color: "#94A3B8",
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    marginBottom: 8,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {customer.email}
                </div>
              )}

              {/* Status badge */}
              <span
                style={{
                  background: badge.bg,
                  color: badge.color,
                  fontSize: 11,
                  fontWeight: 700,
                  padding: "3px 9px",
                  borderRadius: 20,
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                }}
              >
                {badge.label}
              </span>
            </div>
          </div>

          {/* Stats strip */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 10,
              marginBottom: 20,
            }}
          >
            {[
              { label: "Orders", value: customer.totalOrders },
              { label: "Total Spend", value: `KSh ${customer.totalSpend.toLocaleString()}` },
              { label: "Avg Order", value: `KSh ${avgSpend.toLocaleString()}` },
            ].map((s) => (
              <div
                key={s.label}
                style={{
                  background: "#F8FAFC",
                  border: "1px solid #E2E8F0",
                  borderRadius: 8,
                  padding: "12px",
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    fontSize: s.label === "Orders" ? 22 : 14,
                    fontWeight: 800,
                    color: "#0F172A",
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    letterSpacing: "-0.3px",
                    marginBottom: 2,
                  }}
                >
                  {s.value}
                </div>
                <div
                  style={{
                    fontSize: 10.5,
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    color: "#94A3B8",
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                  }}
                >
                  {s.label}
                </div>
              </div>
            ))}
          </div>

          {/* Core details */}
          <div style={{ marginBottom: 20 }}>
            <DetailRow label="Location"    value={customer.location} />
            <DetailRow label="Date Joined" value={customer.dateJoined} />
            <DetailRow label="Last Active" value={relativeTime(customer.lastActive)} />
            <DetailRow label="Customer ID" value={customer.id} mono />
          </div>

          {/* ── Behavioral Insight Block ── */}
          <div
            style={{
              background: "#F8FAFC",
              border: "1px solid #E2E8F0",
              borderRadius: 12,
              padding: "16px",
              marginBottom: 20,
            }}
          >
            <SectionLabel>Customer Behavior</SectionLabel>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 10,
              }}
            >
              {[
                {
                  label: "Avg Booking Value",
                  value: `KSh ${customer.behaviorStats.avgBookingValue.toLocaleString()}`,
                },
                {
                  label: "Booking Frequency",
                  value: customer.behaviorStats.bookingFrequency,
                },
                {
                  label: "Top Category",
                  value: customer.behaviorStats.topCategory,
                },
                {
                  label: "Cancellation Rate",
                  value: `${customer.behaviorStats.cancellationRate}%`,
                  highlight:
                    customer.behaviorStats.cancellationRate > 20
                      ? "#DC2626"
                      : customer.behaviorStats.cancellationRate > 10
                      ? "#D97706"
                      : "#059669",
                },
              ].map((stat) => (
                <div
                  key={stat.label}
                  style={{
                    background: "#fff",
                    borderRadius: 8,
                    padding: "10px 12px",
                    border: "1px solid #F1F5F9",
                  }}
                >
                  <div
                    style={{
                      fontSize: 10.5,
                      fontWeight: 600,
                      color: "#94A3B8",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      fontFamily: "'Plus Jakarta Sans', sans-serif",
                      marginBottom: 4,
                    }}
                  >
                    {stat.label}
                  </div>
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 700,
                      color: stat.highlight || "#0F172A",
                      fontFamily: "'Plus Jakarta Sans', sans-serif",
                    }}
                  >
                    {stat.value}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ── Recent Bookings ── */}
          {customer.recentBookings.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <SectionLabel>Recent Bookings</SectionLabel>
              {customer.recentBookings.map((booking, idx) => {
                const bStyle =
                  BOOKING_STATUS_STYLES[booking.status] ||
                  BOOKING_STATUS_STYLES.pending;
                return (
                  <div
                    key={booking.id}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      padding: "10px 0",
                      borderBottom:
                        idx < customer.recentBookings.length - 1
                          ? "1px solid #F8FAFC"
                          : "none",
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: "#0F172A",
                          fontFamily: "'Plus Jakarta Sans', sans-serif",
                          marginBottom: 2,
                        }}
                      >
                        {booking.service}
                      </div>
                      <div
                        style={{
                          fontSize: 11.5,
                          color: "#94A3B8",
                          fontFamily: "'Plus Jakarta Sans', sans-serif",
                        }}
                      >
                        {booking.pro} · {booking.date}
                      </div>
                    </div>
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: bStyle.color,
                        fontFamily: "'Plus Jakarta Sans', sans-serif",
                        whiteSpace: "nowrap",
                        marginLeft: 12,
                        marginTop: 2,
                      }}
                    >
                      {bStyle.label}
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          {/* ── Reviews Given ── */}
          {customer.reviews.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <SectionLabel>Reviews Given</SectionLabel>
              {customer.reviews.slice(0, 4).map((review, idx) => (
                <div
                  key={idx}
                  style={{
                    background: "#F8FAFC",
                    borderRadius: 8,
                    padding: "12px",
                    marginBottom: 8,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: 5,
                    }}
                  >
                    <span
                      style={{
                        fontSize: 12.5,
                        fontWeight: 600,
                        color: "#334155",
                        fontFamily: "'Plus Jakarta Sans', sans-serif",
                      }}
                    >
                      {review.pro}
                    </span>
                    {/* Star rating */}
                    <div style={{ display: "flex", gap: 2 }}>
                      {[...Array(5)].map((_, i) => (
                        <svg
                          key={i}
                          width="11"
                          height="11"
                          viewBox="0 0 24 24"
                          fill={i < review.rating ? "#F59E0B" : "#E2E8F0"}
                          stroke={i < review.rating ? "#F59E0B" : "#E2E8F0"}
                          strokeWidth="1"
                        >
                          <polygon points="12 17.27 18.18 21 16.54 13.97 22 9.24 14.81 8.63 12 2 9.19 8.63 2 9.24 7.46 13.97 5.82 21 12 17.27" />
                        </svg>
                      ))}
                    </div>
                  </div>
                  <div
                    style={{
                      fontSize: 12.5,
                      color: "#64748B",
                      lineHeight: 1.5,
                      fontFamily: "'Plus Jakarta Sans', sans-serif",
                    }}
                  >
                    {review.text}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Empty reviews state */}
          {customer.reviews.length === 0 && (
            <div style={{ marginBottom: 20 }}>
              <SectionLabel>Reviews Given</SectionLabel>
              <div
                style={{
                  fontSize: 13,
                  color: "#CBD5E1",
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  padding: "8px 0",
                }}
              >
                No reviews left yet.
              </div>
            </div>
          )}
        </div>

        {/* ── Sticky footer actions ── */}
        <div
          style={{
            flexShrink: 0,
            borderTop: "1px solid #E2E8F0",
            padding: "16px 24px",
            background: "#fff",
          }}
        >
          <div
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}
          >
            <button
              onClick={handleSuspend}
              style={{
                padding: "10px 16px",
                background:
                  customer.status === "suspended" ? "#0F172A" : "#fff",
                border:
                  customer.status === "suspended"
                    ? "none"
                    : "1px solid #E2E8F0",
                borderRadius: 8,
                fontSize: 13,
                fontWeight: 600,
                color: customer.status === "suspended" ? "#fff" : "#64748B",
                cursor: "pointer",
                fontFamily: "'Plus Jakarta Sans', sans-serif",
              }}
            >
              {customer.status === "suspended"
                ? "⊙ Reactivate"
                : "⊘ Suspend"}
            </button>

            <button
              onClick={handleFlag}
              style={{
                padding: "10px 16px",
                background: "#fff",
                border: `1px solid ${customer.status === "flagged" ? "#BBF7D0" : "#FCA5A5"}`,
                borderRadius: 8,
                fontSize: 13,
                fontWeight: 600,
                color: customer.status === "flagged" ? "#059669" : "#DC2626",
                cursor: "pointer",
                fontFamily: "'Plus Jakarta Sans', sans-serif",
              }}
            >
              {customer.status === "flagged" ? "✓ Clear Flag" : "⚑ Flag Account"}
            </button>

            <button
              onClick={() => showToast("Message feature coming soon", "info")}
              style={{
                padding: "10px 16px",
                background: "#fff",
                border: "1px solid #E2E8F0",
                borderRadius: 8,
                fontSize: 13,
                fontWeight: 600,
                color: "#64748B",
                cursor: "pointer",
                fontFamily: "'Plus Jakarta Sans', sans-serif",
              }}
            >
              ✉ Message
            </button>

            <button
              onClick={() => showToast("Full history coming soon", "info")}
              style={{
                padding: "10px 16px",
                background: "#fff",
                border: "1px solid #E2E8F0",
                borderRadius: 8,
                fontSize: 13,
                fontWeight: 600,
                color: "#64748B",
                cursor: "pointer",
                fontFamily: "'Plus Jakarta Sans', sans-serif",
              }}
            >
              ↗ Full History
            </button>
          </div>
        </div>
      </div>
    </>
  );
}